import { Component, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import {
    AbstractControl,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup, ValidationErrors,
    Validators,
} from '@angular/forms';
import { UpAndDownloadComponent } from '../../common/upload/up-and-download-component';
import { VorgangsTyp } from '../../domain/model/vorgangsTyp';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { EicPrefixedExcelxValidator } from './eic-prefixed-excelx-validator';
import { formatDate, JsonPipe } from '@angular/common';
import { Aufgabe } from '../../domain/model/aufgabe';
import { Organisation } from '../../service/organisation/organisation';
import { OrganisationService } from '../../service/organisation/organisation.service';
import { Zuweisung } from '../../domain/model/zuweisung';
import { AuthenticationService } from '../../service/auth/authentication.service';
import { ConfirmDialogService } from '../../service/common/confirm-dialog/confirm-dialog.service';
import { NeueAufgabe } from '../../service/aufgabe/neue-aufgabe';
import { AufgabeService } from '../../service/aufgabe/aufgabe.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AufgabeTypeReadablePipe } from '../../common/aufgabe-type-readable/aufgabe-type-readable.pipe';
import { ErinnerungsMail } from '../../domain/model/erinnerungsmail';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import moment from 'moment';

@Component({
    selector: 'ufla-batch-vorgang-dialog',
    templateUrl: './batch-vorgang-dialog.component.html',
    styleUrls: [
        './batch-vorgang-dialog.component.scss',
    ],
    standalone: false,
})
export class BatchVorgangDialogComponent extends UpAndDownloadComponent implements OnInit {
    readonly dialogRef = inject<MatDialogRef<BatchVorgangDialogComponent>>(MatDialogRef);
    private _formBuilder = inject(UntypedFormBuilder);
    private readonly organisationService = inject(OrganisationService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly confirmDialogService = inject(ConfirmDialogService);
    private readonly dialog = inject(MatDialog);
    private readonly snackBar = inject(MatSnackBar);
    private readonly aufgabeService = inject(AufgabeService);

  @ViewChild('errorDialog', { static: false })
      errorDialogTemplate: TemplateRef<any>;

  isEditable = true;

  public fileValidatorOptions = { maxFileSizeBytes: 150 * 1024 * 1024 };

  validateFiles = (control: AbstractControl): ValidationErrors | null => {
      this.onEicFilesChanged(control.value);
      return this.invalidFileErrors;
  };

  firstFormGroup: UntypedFormGroup = this._formBuilder.group({
      type: [
          '',
          Validators.required,
      ],
  });
  secondFormGroup = this._formBuilder.group({
      kurzbeschreibung: [
          '',
      ],
      name: [
          '',
          Validators.required,
      ],
      emailBenachrichtigungSenden: [
          false,
      ],
      emailBenachrichtigungNachVorgangabschluss: [
          false,
      ],
      erinnerungsMailSenden: [
          false,
      ],
      frist: [
          '',
          Validators.required,
      ],
      orgaTypeLabel: [
          true,
          Validators.required,
      ],
  });
  thirdFormGroup = this._formBuilder.group({
      eonFile: [
          [],
          [
              Validators.required,
              this.validateFiles,
          ],
      ],
  });
  fourthFormGroup = this._formBuilder.group({
      useFileCheckboxes: this._formBuilder.array([]),
  });

  public errorMessage: string;
  public readonly vorgangsTyps = [
      VorgangsTyp.GESAMTLAST,
      VorgangsTyp.ABWURFPUNKTE_INFORMATION,
      VorgangsTyp.ABWURFPUNKTE_AUFGABE,
  ];
  selectedValidFiles: string[] = [];
  invalidFileErrorMessages: string[] = [];
  invalidFileErrors: any[] | null = null;
  invalidFileNamesOrgNotFound: string[] = [];
  controlIndexOrgMap: Map<number, Organisation> = new Map();
  neuerVorgang: Aufgabe = new Aufgabe();
  minFristDate = new Date();  // init minDate ensures selection in the past is prevented by matDatPicker;
  private currrentUserOrg: Organisation;
  private availableOrgs: Organisation[];

  constructor() {
      super();
      this.authenticationService.user$
          .pipe(takeUntilDestroyed())
          .subscribe(user => {
              this.organisationService.getOrganisation(user.organisationsId)
                  .pipe(takeUntilDestroyed())
                  .subscribe(org => {
                      this.currrentUserOrg = org;
                  });
              this.organisationService.getOrganisationen()
                  .pipe(takeUntilDestroyed())
                  .subscribe(organisations => {
                      this.availableOrgs = organisations;
                  });
          });
  }

  ngOnInit(): void {
      this.firstFormGroup.controls.type.valueChanges.subscribe(vc => {
          this.neuerVorgang = BatchVorgangDialogComponent.createBatchVorgangFromType(vc);
          this.patchValuesFromModelToForm();
      });
      this.secondFormGroup.valueChanges.subscribe(() => {
          if (this.secondFormGroup.dirty) {
              this.mapTabFormToModel(2);
          }
      });
      this.thirdFormGroup.valueChanges.subscribe(async () => {
          if (!this.thirdFormGroup.controls.eonFile.invalid) {
              this.useFileCheckboxes.clear();
              this.neuerVorgang.zuweisungen = [];

              this.selectedValidFiles.map((filename, i) => {
                  const organisation = this.getOrganisationFromFileName(filename);
                  if (organisation == null) {
                      console.error('Organisation nicht gefunden für Datei: ' + filename);
                      return;
                  }
                  this.controlIndexOrgMap.set(i, organisation);
                  const useFileCheckbox = this._formBuilder.group({
                      useFileCheckbox: new UntypedFormControl(!organisation.zuweisungDeaktiviert),
                  }, [
                      Validators.requiredTrue,
                  ]);
                  if (organisation.zuweisungDeaktiviert) useFileCheckbox.disable();
                  this.useFileCheckboxes.push(useFileCheckbox);
              });
          } else {
              console.debug(new JsonPipe().transform(this.thirdFormGroup.controls.eonFile.errors));
          }
      });
      this.fourthFormGroup.valueChanges.subscribe(() => {
          this.mapTabFormToModel(4);
      });
  }

  public isFileRequiredError(): boolean {
      const control = this.thirdFormGroup.controls.eonFile;
      return (control.touched || control.dirty) && control.hasError('required');
  }

  public isFileSizeError(): boolean {
      return this.thirdFormGroup.controls.eonFile.hasError('maxFileSize');
  }

  private validateOrganisationsExists(filenames: string[]) {
      this.selectedValidFiles = [];
      this.invalidFileNamesOrgNotFound = [];
      filenames.forEach(filename => {
          const organisation = this.getOrganisationFromFileName(filename);
          if (organisation != null) {
              this.selectedValidFiles.push(filename);
          } else {
              this.invalidFileNamesOrgNotFound.push(filename);
          }
      });
  }

  private patchValuesFromModelToForm() {
      // second page
      this.secondFormGroup.controls.name.patchValue(this.neuerVorgang.name);
      this.secondFormGroup.controls.frist.patchValue(moment(this.neuerVorgang.frist));
      this.secondFormGroup.controls.kurzbeschreibung.patchValue(this.neuerVorgang.kurzbeschreibung);
      this.secondFormGroup.controls.erinnerungsMailSenden.patchValue(true);
      this.secondFormGroup.controls.emailBenachrichtigungSenden.patchValue(this.neuerVorgang.emailBenachrichtigungSenden);
      this.secondFormGroup.controls.emailBenachrichtigungNachVorgangabschluss.patchValue(this.neuerVorgang.emailBenachrichtigungNachVorgangabschluss);

  }

  private getOrganisationFromEicNumber(eicNumber: string): Organisation {
      let organisation = this.availableOrgs.find(o => o.eicNummer === eicNumber);
      if (organisation == null) {
          console.error('Eic-Nummer nicht gefunden: ' + eicNumber);
      }
      return organisation;
  }

  private getOrganisationFromFileName(fileName: string): Organisation {
      const eicNumber = this.getEICNumberFromFileName(fileName);
      if (!eicNumber) {
          return null;
      }

      return this.getOrganisationFromEicNumber(eicNumber);
  }

  getEICNumberFromFileName(fileName: string): string {
      const index = fileName.indexOf('_');
      if (index > -1) {
          return fileName.substring(0, index);
      }

      return '';
  }

  private mapTabFormToModel(tabIndex: number) {
      if (tabIndex == 1) {
          this.neuerVorgang.type = this.firstFormGroup.controls.type.value;
      }
      if (tabIndex == 2) {
          this.neuerVorgang.name = this.secondFormGroup.controls.name.value;
          this.neuerVorgang.frist = this.secondFormGroup.controls.frist.value.toISOString();
          this.neuerVorgang.kurzbeschreibung = this.secondFormGroup.controls.kurzbeschreibung.value;
          this.neuerVorgang.emailBenachrichtigungSenden = this.secondFormGroup.controls.emailBenachrichtigungSenden.value;
          this.neuerVorgang.emailBenachrichtigungNachVorgangabschluss =
        this.secondFormGroup.controls.emailBenachrichtigungNachVorgangabschluss.value;
          if (this.secondFormGroup.controls.erinnerungsMailSenden.value) {
              this.neuerVorgang.erinnerungsMail = new ErinnerungsMail();
          } else {
              this.neuerVorgang.erinnerungsMail = null;
          }

      }
      if (tabIndex == 4) {
          this.neuerVorgang.zuweisungen = [];
          this.neuerVorgang.hatDokument = this.selectedValidFiles?.length > 0;
          this.useFileCheckboxes.controls.map((c, orgIndex) => {
              if (!this.controlIndexOrgMap.get(orgIndex).zuweisungDeaktiviert && c.value.useFileCheckbox) {
                  const zuw = new Zuweisung();
                  zuw.aufgabe = null;  // circle
                  zuw.gelesen = false;
                  zuw.zugewiesen = this.controlIndexOrgMap.get(orgIndex);
                  zuw.dokumentVorhanden = false;
                  zuw.ersteller = this.currrentUserOrg;
                  zuw.replyFileName = '';
                  this.neuerVorgang.zuweisungen.push(zuw); // only for display
              }
          });
      }
  }

  private static createBatchVorgangFromType(vorgangsType: VorgangsTyp): Aufgabe {
      let aufgabeTypeReadablePipe = new AufgabeTypeReadablePipe();
      const vorgang = new Aufgabe();
      vorgang.type = vorgangsType;
      vorgang.name = aufgabeTypeReadablePipe.transform(vorgang.type) + ' ' + (new Date().getFullYear() - 1);

      switch (vorgangsType) {
          case VorgangsTyp.GESAMTLAST:
              vorgang.kurzbeschreibung = 'Angefügt erhalten Sie die Werte der Gesamtlast. Bitte bestätigen Sie den Erhalt ' +
          'über die Schaltfläche „Zur Kenntnis genommen“.';
              break;
          case VorgangsTyp.ABWURFPUNKTE_INFORMATION:
              vorgang.kurzbeschreibung = 'Angefügt erhalten Sie die Werte der Abwurfpunkte zur Information. Nähere ' +
          'Erläuterungen dazu finden Sie im angefügten Dokument. Bitte bestätigen Sie den Erhalt über die ' +
          'Schaltfläche „Zur Kenntnis genommen“.';
              break;
          case VorgangsTyp.ABWURFPUNKTE_AUFGABE:
              vorgang.kurzbeschreibung = 'Angefügt erhalten Sie die Werte der Abwurfpunkte (Aufgabe). Dabei sind ' +
          'erforderliche Anpassungen durch Sie als Netzbetreiber durchzuführen. Nähere Erläuterungen dazu finden Sie' +
          ' im angefügten Dokument. Bitte setzten Sie die erforderlichen Anpassungen um und laden anschließend das' +
          ' ausgefüllte Dokument wieder hoch und schließen den Vorgang über die Schaltfläche „Vorgang erledigen“ ab.';
              break;
          default:
              vorgang.kurzbeschreibung = '';
              break;
      }
      vorgang.frist = new Date().toISOString();
      vorgang.emailBenachrichtigungSenden = true;
      vorgang.emailBenachrichtigungNachVorgangabschluss = true;
      vorgang.erinnerungsMail = new ErinnerungsMail();
      vorgang.mitUenbTeilen = false;
      vorgang.zuweisungen = [];
      return vorgang;
  }

  get useFileCheckboxes() {
      return this.fourthFormGroup.controls['useFileCheckboxes'] as UntypedFormArray;
  }

  public prepareBatchAufgabeForBackend(): string {

      const neueAufgabe = NeueAufgabe.createEmptyNeueAufgabe();
      neueAufgabe.name = this.neuerVorgang.name;
      neueAufgabe.frist = this.secondFormGroup.controls.frist.value;
      neueAufgabe.mitUenbTeilen = false;
      neueAufgabe.emailBenachrichtigungSenden = this.neuerVorgang.emailBenachrichtigungSenden;
      neueAufgabe.emailBenachrichtigungNachVorgangabschluss = this.neuerVorgang.emailBenachrichtigungNachVorgangabschluss;
      neueAufgabe.erinnerungsMailSenden = this.neuerVorgang.erinnerungsMail != null;
      neueAufgabe.kurzbeschreibung = this.neuerVorgang.kurzbeschreibung;
      neueAufgabe.type = this.neuerVorgang.type;
      neueAufgabe.batchZuweisungen = this.neuerVorgang.zuweisungen.map(zw => zw.zugewiesen);

      return JSON.stringify(neueAufgabe);
  }

  public async saveAufgabe() {
      if (await this.confirmDialogService.showDialog(
          'Wollen Sie diese Aufgabe speichern? Eine nachträgliche Änderung ist nicht mehr möglich.',
          'Speichern?',
          'primary',
          'Ja',
          'Nein')) {

          this.firstFormGroup.disable({ emitEvent: false });
          this.secondFormGroup.disable({ emitEvent: false });
          this.thirdFormGroup.disable({ emitEvent: false });
          this.fourthFormGroup.disable({ emitEvent: false });
          let aufgabeCreationFailed = false;

          const stringifiedAufgabeJson = this.prepareBatchAufgabeForBackend();
          const parsedAufgabeToBeSent = JSON.parse(stringifiedAufgabeJson);

          try {
              const aufgabe = await this.aufgabeService.createAufgabe(parsedAufgabeToBeSent);
              const filesToUpload = this.thirdFormGroup.controls.eonFile.value as File[] | null;

              if (!aufgabe) {
                  aufgabeCreationFailed = true;
                  console.error('Anlegen des Vorgangs fehlgeschlagen.');
              }

              // on success and if a file was provided by the user, upload the file
              if (!aufgabeCreationFailed && filesToUpload && filesToUpload.length > 0) {
                  this.numberOfFilesToUpload = filesToUpload.length;
                  for (const file of filesToUpload) {
                      // found during #183218482, additionally fix upload documents without a valid assignee
                      const eicFromUploadFile = this.getEICNumberFromFileName(file.name);
                      if (aufgabe.zuweisungen.map(zw => zw.ersteller).find(org => org.eicNummer == eicFromUploadFile) != null) {
                          const attached = await this.aufgabeService.attachDocument(aufgabe, file, this);
                          if (!attached) {
                              aufgabeCreationFailed = true; // if the creation fails, don't send mails
                              console.error(`dokument ${file.name} NICHT angelegt..`);
                              break;
                          } else {
                              this.numberOfUploadedFiles = filesToUpload.indexOf(file) + 1;
                          }
                      }
                  }
              }

              // send reminder and notifications if desired and all previous calls were successful.
              if (!aufgabeCreationFailed && (aufgabe.emailBenachrichtigungSenden || aufgabe.erinnerungsMail)) {
                  const mailsSend = await this.aufgabeService.sendMailsForAufgaben(aufgabe.id);
                  if (!mailsSend) {
                      aufgabeCreationFailed = true;
                      console.error('Fehler beim versenden der E-Mails zum Vorgang.');
                  }
              }

              if (aufgabeCreationFailed) {
                  this.dialog.open(this.errorDialogTemplate);
                  await this.aufgabeService.deleteAufgabe(aufgabe.id.toString(), false);
              } else {
                  this.snackBar.open('Aufgabe erfolgreich im Jahr '
            + formatDate(this.secondFormGroup.controls.frist.value, 'yyyy', 'DE') + ' angelegt.', 'Ok', {
                      duration: 5000,
                  });
              }

              this.dialogRef.close();
          } catch (error) {
              console.error(error);
              this.errorMessage = error.message;
              this.dialog.open(this.errorDialogTemplate);

              this.firstFormGroup.enable();
              this.secondFormGroup.enable();
              this.thirdFormGroup.enable();
              this.fourthFormGroup.enable();
          }
      }
  }

  closeErrorDialog() {
      this.dialogRef.close();
  }

  toggledEmailBenachrichtigungSenden($event: MatSlideToggleChange) {
      if (!$event.checked && this.secondFormGroup.controls['erinnerungsMailSenden'].value) {
          this.secondFormGroup.controls['erinnerungsMailSenden'].patchValue(false);
          this.mapTabFormToModel(2);
      }
      if ($event.checked) {
          this.secondFormGroup.controls['erinnerungsMailSenden'].enable();
      } else {
          this.secondFormGroup.controls['erinnerungsMailSenden'].disable();
      }
  }

  private onEicFilesChanged(files: File[]): void {
      if (!this.thirdFormGroup) return;

      this.validateOrganisationsExists(files.map(file => file.name));

      const errors: any = {};
      const errorMessages: string[] = [];

      if (this.invalidFileNamesOrgNotFound.length) {
          errors.organisationNotFound = this.invalidFileNamesOrgNotFound;
          errorMessages.push('Organisation nicht gefunden für: ' + errors.organisationNotFound.join(', '));
      }

      const eicErrorResult = EicPrefixedExcelxValidator.areFileNamesEicPrefixedAndExcel()(this.thirdFormGroup.controls.eonFile);
      if (eicErrorResult && eicErrorResult.eicErrors?.invalidFiles.length > 0) {

          errors.duplicateFileNames = eicErrorResult.eicErrors.duplicateFileNames || [];
          errors.eicSyntaxErrors = eicErrorResult.eicErrors.eicSyntaxErrors || [];
          errors.eicFileTypeErrors = eicErrorResult.eicErrors.eicFileNameExtensionErrors || [];
          if (errors.duplicateFileNames.length) {
              errorMessages.push('EIC kommt doppelt vor: ' + errors.duplicateFileNames.join(', '));
          }
          if (errors.eicSyntaxErrors.length) {
              errorMessages.push('Syntax-Fehler: ' + errors.eicSyntaxErrors.join(', '));
          }
          if (errors.eicFileTypeErrors.length) {
              errorMessages.push('Dateierweiterung ungültig: ' + errors.eicFileTypeErrors.join(', '));
          }
      }

      if (errorMessages.length > 0) {
          this.invalidFileErrorMessages = errorMessages;
          this.invalidFileErrors = errors;
      } else {
          this.invalidFileErrorMessages = [];
          this.invalidFileErrors = null;
      }
  }

}
